<script>
import {mapActions, mapGetters} from "vuex";
import { monthMap } from "@/lib";
import { metricEventTypes, metricParents } from "@/lib";
import KonzessionsAbgabenRow from "@/components/ui/KonzessionsAbgabenRow.vue";
import {resolveBreadcrumbItems} from "@/mixins/resolveBreadcrumbItems";
import Breadcrumbs from "@/components/ui/Breadcrumbs.vue";

export default {
  name: "Konzessionsabgaben",
  components: {Breadcrumbs, KonzessionsAbgabenRow},
  mixins: [resolveBreadcrumbItems],
  data: () => ({
    header: require("@/assets/konzessionsabgaben-header.png"),
    //  monthItems: [{text: "Alle Monate", value: "Alle Monate"}, ...Object.entries(months).map(e => ({text: e[1], value: e[0]}))],
    selectedCommune: "Alle Kommunen",
    selectedDocumentType: "Alle Dokumente",
    selectedYear: "Alle Jahre",
    selectedMonth: "Alle Monate",
    showFilter: false,
    pageSize: 25,
  }),
  computed: {
    ...mapGetters("user", ["isNewEmployee"]),
    ...mapGetters("konzessionsthemen", [
      "getKonzessionsabgaben",
      "getDocumentYears",
      "getDocumentTypes",
      "getDocumentCommunes",
      "getKonzessionsabgabenPagination",
      "getKonzessionsabgabenLoading"
    ]),
    currentPage() {
      return this.getKonzessionsabgabenPagination?.page ?? 1;
    },
    hasMoreKonzessionsabgaben() {
      return this.getKonzessionsabgabenPagination?.hasMore ?? false;
    },
    enabledMonthDropdown() {
      return this.selectedDocumentType === 'Vorauszahlung';
    },
    monthItems() {
      const items = [{text: "Alle Monate", value: "Alle Monate"}];

      const unsortedMonths = [];
      for (const key of Object.keys(monthMap)) {
        unsortedMonths.push({text: monthMap[key], value: key});
      }

      unsortedMonths.sort((a, b) => parseInt(a.value) - parseInt(b.value));

      items.push(...unsortedMonths);
      return items;
    },
    filteredItems() {
      return this.getKonzessionsabgaben
          .filter(item => this.selectedCommune === "Alle Kommunen"|| item.kommune === this.selectedCommune)
          .filter(item => this.selectedYear === "Alle Jahre"|| item.jahr === this.selectedYear)
          .filter(item => this.selectedDocumentType === "Alle Dokumente"|| item.dokumenttyp === this.selectedDocumentType)
          .filter(item => this.selectedMonth === "Alle Monate" || (this.enabledMonthDropdown && item.monat && item.monat === this.selectedMonth));
    },
    headerStyle() {
      return {
        'font-size': this.$vuetify.breakpoint.smAndUp ? '40px' : '2rem'
      }
    },
  },
  methods: {
    ...mapActions("konzessionsthemen", ["loadKonzessionsabgaben", "loadKonzessionsabgabenFilterValues"]),
    ...mapActions("metrics", ["postHeartbeat"]),
    loadKonzessionsabgabenPage({ page, append } = {}) {
      const filters = {};

      if (this.selectedCommune !== "Alle Kommunen") {
        filters.kommune = this.selectedCommune;
      }

      if (this.selectedYear !== "Alle Jahre") {
        filters.jahr = this.selectedYear;
      }

      if (this.selectedDocumentType !== "Alle Dokumente") {
        filters.dokumenttyp = this.selectedDocumentType;
      }

      if (this.enabledMonthDropdown && this.selectedMonth !== "Alle Monate") {
        filters.monat = this.selectedMonth;
      }

      return this.loadKonzessionsabgaben({
        page,
        pageSize: this.pageSize,
        append,
        filters
      });
    },
    onDoctypeChange() {
      if (this.selectedDocumentType !== 'Vorauszahlung') {
        this.selectedMonth = 'Alle Monate';
      }

      this.onFilterChange();
    },
    onFilterChange() {
      this.loadKonzessionsabgabenPage({ page: 1, append: false });
    },
    loadMore() {
      if (this.getKonzessionsabgabenLoading || !this.hasMoreKonzessionsabgaben) {
        return;
      }

      this.loadKonzessionsabgabenPage({ page: this.currentPage + 1, append: true });
    }
  },
  mounted() {
    this.postHeartbeat({
      name: 'Konzessionsabgaben',
      parent: metricParents.NONE,
      type: metricEventTypes.PAGE_VISIT,
    })
    this.loadKonzessionsabgabenFilterValues();
    this.loadKonzessionsabgabenPage({ page: 1, append: false })
  }
}
</script>

<template>
  <div class="max-w-container">
    <v-img :src="header" class="mb-5 testate-header" gradient="to top, #000000CE, #54545422">
      <v-row no-gutters class="fill-height pa-2 px-5 d-flex align-end">
        <v-col cols="12">
          <div class="custom-header-text mb-2 white--text new-font-bold" :style="headerStyle">Konzessionsabgaben</div>
        </v-col>
      </v-row>
    </v-img>
    <div class="pa-5">
    <v-row>
      <div style="width: 100%">
        <breadcrumbs />
      </div>
      <template v-if="$vuetify.breakpoint.mdAndUp || showFilter">
        <v-col cols="12" md="3" v-if="isNewEmployee">
          <v-select v-model="selectedCommune" :items="getDocumentCommunes"
                    @change="onFilterChange"
                    background-color="rgb(232,212,223)" color="primary"
                    outlined single-line hide-details filled dense/>
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="selectedDocumentType" :items="getDocumentTypes"
                    @change="onDoctypeChange"
                    background-color="rgb(232,212,223)" color="primary"
                    outlined single-line hide-details filled dense/>
        </v-col>
        <v-col cols="12" md="2">
          <v-select v-model="selectedYear" :items="getDocumentYears"
                    @change="onFilterChange"
                    background-color="rgb(232,212,223)" color="primary"
                    outlined single-line hide-details filled dense/>
        </v-col>
        <v-col cols="12" md="2">
          <v-select :disabled="!enabledMonthDropdown" v-model="selectedMonth" :items="monthItems"
                    @change="onFilterChange"
                    background-color="rgb(232,212,223)" color="primary"
                    outlined single-line hide-details filled dense/>
        </v-col>
        <v-col cols="12" md="2">
            <v-tooltip bottom max-width="300px" color="rgb(29,38,48)" content-class="mr-3">
              <template v-slot:activator="{ on, attrs }">
                <div class="d-flex primary-text fill-height align-center justify-end">
                  <span v-bind="attrs" v-on="on">
                    <v-icon color="primary" small>mdi-help-circle-outline</v-icon> Monatswahl
                  </span>
                </div>
              </template>
              <span>Die Monatswahl ist nur bei Vorauszahlung für Gas, Strom und Wasser verfügbar. In diesem Fall können einzelne Monate seperat gewählt werden.</span>
            </v-tooltip>
        </v-col>

      </template>
      <v-col cols="12" v-if="$vuetify.breakpoint.smAndDown">
        <v-btn
            @click="showFilter = !showFilter"
            block
            outlined
            color="primary"
        >
          <v-icon color="primary">mdi-chevron-{{ showFilter ? "up" : "down" }}</v-icon>
          Filter {{ showFilter ? "ausblenden" : "anzeigen" }}
        </v-btn>
      </v-col>
    </v-row>
      <v-sheet color="primary" class="pa-3 mt-5" style="border-top-left-radius: 5px; border-top-right-radius: 5px;">
      <v-row class="header-row">
        <v-col cols="3" class="py-0 px-2 d-flex align-center">
          Kommune
        </v-col>
        <v-col cols="3" class="py-0 px-2 d-flex align-center">
          Dokument
        </v-col>
        <v-col cols="1" class="py-0 px-2 d-flex align-center justify-end pr-5">
          <span style="width: 50px; text-align: left;">
            Jahr
          </span>
        </v-col>
        <v-col cols="1" class="py-0 px-2 d-flex align-center">
          Monat
        </v-col>
        <v-col cols="2" class="py-0 px-2 d-flex align-center">
          Download
        </v-col>
        <v-col cols="2" class="py-0 px-2 d-flex align-center">
          Status
        </v-col>
      </v-row>
      </v-sheet>

      <konzessions-abgaben-row  v-for="abgabe of filteredItems" :key="abgabe.osid" :konzessionsabgabe="abgabe" />
      <div class="d-flex justify-center mt-6" v-if="hasMoreKonzessionsabgaben">
        <v-btn
          color="primary"
          outlined
          :loading="getKonzessionsabgabenLoading"
          :disabled="getKonzessionsabgabenLoading"
          @click="loadMore"
        >
          Mehr Anzeigen
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.testate-header {
  aspect-ratio: 960/288;
  background: transparent linear-gradient(180deg, #000000CE 0%, #54545422 100%) 0% 0% no-repeat padding-box;
}

.header-title {
  font-weight: bold;
}


.legend-table th {
  color: white !important;
  height: 32px !important;
  font-size: 16px !important;
}

.header-row div{
  color: white;
  font-size: 16px;
  height: 32px;
}
</style>
